/**
 * Supabase client — authentication only.
 *
 * IMPORTANT: the browser talks to Supabase ONLY to sign in and to hold a session.
 * It never reads or writes application data through Supabase. All data flows
 * through our own API, which independently verifies the JWT server-side. That
 * keeps a single place where authorisation decisions are made.
 *
 * Only the anon key is used here, which is safe to ship to the browser. A
 * service-role key must never appear in frontend code.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { ENV } from './env';

const url = ENV.supabaseUrl;
const anonKey = ENV.supabaseAnonKey;

/**
 * True when Supabase is configured. The app is developed before credentials
 * exist, so we surface a clear message on the login screen instead of throwing
 * during module import and rendering a blank page.
 */
export const isSupabaseConfigured = Boolean(url && anonKey);

export const supabase: SupabaseClient = createClient(
  url || 'http://localhost:54321',
  anonKey || 'anon-key-not-configured',
  {
    auth: {
      // sessionStorage, not localStorage (the default): the session survives a
      // RELOAD of the same tab but ends when the tab/window closes — closing
      // the app signs the user out, as required. Each tab is its own session,
      // so a fresh tab asks for credentials again. The 30-minute inactivity
      // timer still ends idle sessions in an open tab.
      persistSession: true,
      storage: window.sessionStorage,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    },
  },
);

/** Current access token, or null. Used to authorise API calls. */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
